import React, { useCallback, useMemo, useState } from 'react'
import noop from 'lodash/noop'
import { useReportUserStoryById, useToggleLikeUserStoryById } from '@/services/StoriesService/hooks'
import { ReactFCC } from '@/types/react'
import { PlayableStory } from '@/utils/StoryUtil'

interface MomentStoryContextState {
  reportUserStoryById: () => void
  toggleLikeUserStoryById: () => void
  selectUserStory: (story: PlayableStory) => void
  clearSelectedUserStory: () => void
  story?: PlayableStory
}

const defaultValue = {
  reportUserStoryById: () => Promise.resolve(),
  toggleLikeUserStoryById: () => Promise.resolve(),
  selectUserStory: noop,
  clearSelectedUserStory: noop,
  story: undefined,
}

export const MomentStoryContext = React.createContext<MomentStoryContextState>(defaultValue)

export const MomentStoryProvider: ReactFCC = ({ children }) => {
  const [story, setStory] = useState<PlayableStory>()
  const { reportUserStoryById } = useReportUserStoryById()
  const { toggleLikeUserStoryById } = useToggleLikeUserStoryById()

  const handleReportUserStoryById = useCallback(() => {
    if (!story?.id) return
    reportUserStoryById(story.id)
  }, [reportUserStoryById, story])

  const handleToggleLikeUserStoryById = useCallback(async () => {
    if (!story?.id) return
    const { data } = await toggleLikeUserStoryById(story.id)

    if (data?.toggleLikeUserStory) {
      setStory({
        ...story,
        ...data.toggleLikeUserStory,
      })
    }
  }, [story, toggleLikeUserStoryById])

  const clearSelectedUserStory = useCallback(() => {
    setStory(undefined)
  }, [])

  const selectUserStory = useCallback((story: PlayableStory) => {
    if (!story) return
    setStory(story)
  }, [])

  const value = useMemo(() => {
    return {
      selectUserStory,
      clearSelectedUserStory,
      reportUserStoryById: handleReportUserStoryById,
      toggleLikeUserStoryById: handleToggleLikeUserStoryById,
      story,
    }
  }, [handleReportUserStoryById, handleToggleLikeUserStoryById, selectUserStory, story, clearSelectedUserStory])

  return <MomentStoryContext.Provider value={value}>{children}</MomentStoryContext.Provider>
}

export const useMomentStory = () => React.useContext(MomentStoryContext)
